// frontend/src/components/Dashboard/PreOrderManager.jsx - COMPLETE FIXED VERSION
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faSearch,
  faEye,
  faTimes,
  faTractor,
  faCheckCircle,
  faClock,
  faMoneyBillWave,
  faCalendarCheck,
  faUser,
  faPhone,
  faMapMarkerAlt,
  faBox,
  faDollarSign,
  faSpinner,
  faWeightScale,
  faGlobe,
  faBoxes,
  faShip,
  faAnchor,
  faCreditCard,
  faMobileAlt,
  faCalendarAlt,
  faCheckDouble,
  faExclamationTriangle,
  faInfoCircle,
  faAward,
  faBuilding,
  faStore,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

// ============================================
// CONSTANTS - Globally Sourced (Confidential)
// ============================================
const SOURCE_LABEL = "Globally Sourced";
const SOURCE_ICON = faGlobe;

// ============================================
// HELPERS
// ============================================
const getStatusColor = (status) => {
  const colors = {
    pending: "#D9A321",
    deposit_received: "#8b5cf6",
    confirmed: "#3b82f6",
    ready_for_pickup: "#10b981",
    completed: "#16a34a",
    cancelled: "#dc2626",
    refunded: "#6b7280",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusLabel = (status) => {
  const labels = {
    pending: "Pending",
    deposit_received: "Deposit Received",
    confirmed: "Confirmed",
    ready_for_pickup: "Ready for Pickup",
    completed: "Completed",
    cancelled: "Cancelled",
    refunded: "Refunded",
  };
  return labels[status] || status;
};

const getStatusIcon = (status) => {
  const icons = {
    pending: faClock,
    deposit_received: faMoneyBillWave,
    confirmed: faCheckCircle,
    ready_for_pickup: faCalendarCheck,
    completed: faCheckDouble,
    cancelled: faTimes,
    refunded: faTimes,
  };
  return icons[status] || faClock;
};

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return "$0.00";
  return `$${Number(amount).toFixed(2)}`;
};

// ============================================
// MAIN COMPONENT
// ============================================
const PreOrderManager = ({
  preOrders = [],
  stats = {},
  onUpdateStatus,
  onRefresh,
  availableSizes = [],
  preOrderSizes = [],
  isLoading = false,
}) => {
  const [filterStatus, setFilterStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPreOrder, setSelectedPreOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  const sizes = preOrderSizes.length > 0 ? preOrderSizes : [25, 50];

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "deposit_received", label: "Deposit Received" },
    { value: "confirmed", label: "Confirmed" },
    { value: "ready_for_pickup", label: "Ready for Pickup" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "refunded", label: "Refunded" },
  ];

  const filteredPreOrders = (Array.isArray(preOrders) ? preOrders : [])
    .filter((p) => !filterStatus || p.status === filterStatus)
    .filter((p) => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        p.preOrderNumber?.toLowerCase().includes(search) ||
        p.customerInfo?.fullName?.toLowerCase().includes(search) ||
        p.customerInfo?.phone?.includes(search) ||
        p.customerInfo?.farmOrBusinessName?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      let aVal = a[sortField] || "";
      let bVal = b[sortField] || "";
      if (sortField === "createdAt") {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });

  const handleStatusUpdate = async (id, status) => {
    setLoading(true);
    try {
      await onUpdateStatus(id, { status });
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error updating pre-order status:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: "Cash",
      mobile_money: "Mobile Money",
      orange_money: "Orange Money",
      afrimoney: "Afrimoney",
      wave: "WAVE",
      bank_transfer: "Bank Transfer",
      credit_card: "Credit Card",
      saved_payment: "Saved Payment",
    };
    return labels[method] || method || "N/A";
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return faClock;
    return sortDirection === "asc" ? faClock : faClock;
  };

  // Stats summary with safe defaults
  const totalPreOrders = stats.totalPreOrders || stats.total || preOrders.length || 0;
  const totalRevenueUsd = stats.totalRevenueUsd || stats.totalRevenue || 0;
  const totalDepositUsd = stats.totalDepositUsd || stats.totalDeposit || 0;
  const totalBalanceUsd = stats.totalBalanceUsd || stats.totalBalance || 0;
  const totalPacks = stats.totalPacks || 0;

  return (
    <div className="preorder-manager">
      {/* Stats Summary */}
      <div className="preorder-stats-grid">
        <div className="preorder-stat-card">
          <div className="stat-value">{totalPreOrders}</div>
          <div className="stat-label">Total Pre-Orders</div>
        </div>
        <div className="preorder-stat-card revenue">
          <div className="stat-value">{formatCurrency(totalRevenueUsd)}</div>
          <div className="stat-label">Total Revenue</div>
        </div>
        <div className="preorder-stat-card deposit">
          <div className="stat-value">{formatCurrency(totalDepositUsd)}</div>
          <div className="stat-label">Total Deposits</div>
        </div>
        <div className="preorder-stat-card balance">
          <div className="stat-value">{formatCurrency(totalBalanceUsd)}</div>
          <div className="stat-label">Balance Due</div>
        </div>
        <div className="preorder-stat-card packs">
          <div className="stat-value">{totalPacks}</div>
          <div className="stat-label">Total Packs</div>
        </div>
        <div className="preorder-stat-card sizes">
          <div className="stat-value">
            <FontAwesomeIcon icon={faWeightScale} /> {sizes.join("kg, ")}kg
          </div>
          <div className="stat-label">
            <FontAwesomeIcon icon={faGlobe} /> Pre-Order Sizes
          </div>
        </div>
        <div className="preorder-stat-card global">
          <div className="stat-value">
            <FontAwesomeIcon icon={faGlobe} />
          </div>
          <div className="stat-label">Globally Sourced</div>
        </div>
      </div>

      {/* Supplier Info - Globally Sourced */}
      <div className="supplier-info-card">
        <FontAwesomeIcon icon={faGlobe} className="supplier-icon" />
        <div className="supplier-info">
          <strong>Premium Products, Globally Sourced</strong>
          <span>
            <FontAwesomeIcon icon={faGlobe} /> Global Supply Chain &bull;
            <FontAwesomeIcon icon={faAward} /> Premium Quality
          </span>
        </div>
        <span className="supplier-badge">
          <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
        </span>
      </div>

      {/* Filters */}
      <div className="section-header">
        <h2>
          <FontAwesomeIcon icon={faClipboardList} /> Pre-Orders
          <span className="origin-badge">
            <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
          </span>
          <span className="preorder-count">
            ({filteredPreOrders.length} {filteredPreOrders.length === 1 ? "order" : "orders"})
          </span>
        </h2>
        <div className="filters">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="search-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, phone, or order #"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          {isLoading && (
            <div className="loading-spinner">
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              <th onClick={() => handleSort("preOrderNumber")}>
                Order # <FontAwesomeIcon icon={getSortIcon("preOrderNumber")} />
              </th>
              <th onClick={() => handleSort("customerInfo.fullName")}>
                Customer <FontAwesomeIcon icon={getSortIcon("customerInfo.fullName")} />
              </th>
              <th>Phone</th>
              <th>Location</th>
              <th>Items</th>
              <th onClick={() => handleSort("subtotal.usd")}>
                Total <FontAwesomeIcon icon={getSortIcon("subtotal.usd")} />
              </th>
              <th>Deposit</th>
              <th>Balance</th>
              <th onClick={() => handleSort("status")}>
                Status <FontAwesomeIcon icon={getSortIcon("status")} />
              </th>
              <th onClick={() => handleSort("createdAt")}>
                Date <FontAwesomeIcon icon={getSortIcon("createdAt")} />
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPreOrders.length === 0 ? (
              <tr>
                <td colSpan="11" className="no-data">
                  <div className="empty-state">
                    <FontAwesomeIcon icon={faClipboardList} size="2x" />
                    <p>No pre-orders found.</p>
                    <span className="sub-text">Try adjusting your filters or search terms.</span>
                  </div>
                </td>
              </tr>
            ) : (
              filteredPreOrders.map((preOrder) => (
                <tr
                  key={preOrder._id}
                  className={preOrder.status === "pending" ? "pending-row" : ""}
                >
                  <td>
                    <strong>{preOrder.preOrderNumber}</strong>
                    <span className="size-badge">
                      <FontAwesomeIcon icon={faBoxes} />{" "}
                      {preOrder.totalPacks || 0} packs
                    </span>
                    <span className="origin-tag">
                      <FontAwesomeIcon icon={faGlobe} />
                    </span>
                  </td>
                  <td>
                    <div className="customer-info">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="customer-icon"
                      />
                      {preOrder.customerInfo?.fullName || "N/A"}
                      {preOrder.customerInfo?.farmOrBusinessName && (
                        <div className="farm-name">
                          <FontAwesomeIcon icon={faTractor} />{" "}
                          {preOrder.customerInfo.farmOrBusinessName}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <FontAwesomeIcon icon={faPhone} className="phone-icon" />
                    <a
                      href={`tel:${preOrder.customerInfo?.phone}`}
                      className="phone-link"
                    >
                      {preOrder.customerInfo?.phone || "N/A"}
                    </a>
                    {preOrder.customerInfo?.whatsapp && (
                      <a
                        href={`https://wa.me/${preOrder.customerInfo.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whatsapp-link"
                      >
                        <FontAwesomeIcon icon={faWhatsapp} />
                      </a>
                    )}
                  </td>
                  <td>
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      className="location-icon"
                    />
                    {preOrder.customerInfo?.location || "N/A"}
                    {preOrder.customerInfo?.district && (
                      <span className="district-tag">
                        {preOrder.customerInfo.district}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="items-info">
                      <FontAwesomeIcon icon={faBox} />
                      {preOrder.totalPacks || 0} packs
                      <span className="item-detail">
                        ({preOrder.totalQuantityKg || 0} kg)
                      </span>
                    </div>
                  </td>
                  <td>
                    <strong className="total-amount">
                      {formatCurrency(preOrder.subtotal?.usd)}
                    </strong>
                  </td>
                  <td className="deposit-amount">
                    {formatCurrency(preOrder.depositRequired?.usd)}
                    {preOrder.payment?.status === "deposit_received" && (
                      <span className="deposit-received-badge">
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </span>
                    )}
                  </td>
                  <td className="balance-amount">
                    {formatCurrency(preOrder.balanceDue?.usd)}
                  </td>
                  <td>
                    <span
                      className="status-badge"
                      style={{ background: getStatusColor(preOrder.status) }}
                    >
                      <FontAwesomeIcon icon={getStatusIcon(preOrder.status)} />
                      {getStatusLabel(preOrder.status)}
                    </span>
                  </td>
                  <td>{formatDate(preOrder.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      <select
                        value={preOrder.status}
                        onChange={(e) =>
                          handleStatusUpdate(preOrder._id, e.target.value)
                        }
                        className="status-select"
                        style={{ borderColor: getStatusColor(preOrder.status) }}
                        disabled={loading || isLoading}
                      >
                        <option value="pending">Pending</option>
                        <option value="deposit_received">
                          Deposit Received
                        </option>
                        <option value="confirmed">Confirmed</option>
                        <option value="ready_for_pickup">
                          Ready for Pickup
                        </option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="refunded">Refunded</option>
                      </select>
                      <button
                        className="view-btn"
                        onClick={() => setSelectedPreOrder(preOrder)}
                        title="View Details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Details Modal */}
      {selectedPreOrder && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedPreOrder(null)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FontAwesomeIcon icon={faClipboardList} /> Pre-Order Details
                <span className="order-number">
                  {selectedPreOrder.preOrderNumber}
                </span>
                <span className="origin-badge">
                  <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                </span>
                <span className="premium-badge">
                  <FontAwesomeIcon icon={faAward} /> Premium Quality
                </span>
              </h2>
              <button
                className="close-btn"
                onClick={() => setSelectedPreOrder(null)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="modal-body">
              {/* Customer Info */}
              <div className="detail-section">
                <h3>
                  <FontAwesomeIcon icon={faUser} /> Customer Information
                </h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Full Name:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.fullName || "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Farm/Business:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.farmOrBusinessName ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">
                      <a
                        href={`tel:${selectedPreOrder.customerInfo?.phone}`}
                        className="phone-link"
                      >
                        {selectedPreOrder.customerInfo?.phone || "N/A"}
                      </a>
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">WhatsApp:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.whatsapp ? (
                        <a
                          href={`https://wa.me/${selectedPreOrder.customerInfo.whatsapp}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="whatsapp-link"
                        >
                          <FontAwesomeIcon icon={faWhatsapp} />{" "}
                          {selectedPreOrder.customerInfo.whatsapp}
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.email || "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.location || "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">District:</span>
                    <span className="detail-value">
                      {selectedPreOrder.customerInfo?.district || "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Farm Info */}
              <div className="detail-section">
                <h3>
                  <FontAwesomeIcon icon={faTractor} /> Farm Information
                </h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Customer Type:</span>
                    <span className="detail-value">
                      {selectedPreOrder.farmInfo?.customerType?.replace(
                        /_/g,
                        " ",
                      ) || "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Animal Types:</span>
                    <span className="detail-value">
                      {selectedPreOrder.farmInfo?.animalTypes?.join(", ") ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Animal Count:</span>
                    <span className="detail-value">
                      {selectedPreOrder.farmInfo?.animalCount || 0}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Feed Mixed at Once:</span>
                    <span className="detail-value">
                      {selectedPreOrder.farmInfo?.feedMixedAtOneTimeKg || 0} kg
                    </span>
                  </div>
                  {selectedPreOrder.farmInfo?.notes && (
                    <div className="detail-item full-width">
                      <span className="detail-label">Notes:</span>
                      <span className="detail-value">
                        {selectedPreOrder.farmInfo.notes}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Items */}
              <div className="detail-section">
                <h3>
                  <FontAwesomeIcon icon={faBox} /> Pre-Order Items
                  <span className="origin-summary">
                    <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                  </span>
                </h3>
                <div className="table-responsive">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Pack Size</th>
                        <th>Qty</th>
                        <th>Regular Price</th>
                        <th>Pre-Order Price</th>
                        <th>Line Total</th>
                        <th>Deposit</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPreOrder.items?.map((item, index) => (
                        <tr key={index}>
                          <td>
                            {item.productName}
                            {item.category && (
                              <span className="category-tag">
                                {item.category.replace(/-/g, " ")}
                              </span>
                            )}
                          </td>
                          <td>{item.packSizeKg} kg</td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.regularPrice?.usd)}</td>
                          <td>{formatCurrency(item.preOrderPrice?.usd)}</td>
                          <td>{formatCurrency(item.lineTotal?.usd)}</td>
                          <td>{formatCurrency(item.depositRequired?.usd)}</td>
                          <td>{formatCurrency(item.balanceDue?.usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="5"></td>
                        <td>
                          <strong>
                            Total:{" "}
                            {formatCurrency(selectedPreOrder.subtotal?.usd)}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {formatCurrency(
                              selectedPreOrder.depositRequired?.usd,
                            )}
                          </strong>
                        </td>
                        <td>
                          <strong>
                            {formatCurrency(selectedPreOrder.balanceDue?.usd)}
                          </strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <div className="summary-box">
                  <span>
                    <FontAwesomeIcon icon={faBoxes} /> Total Packs:{" "}
                    {selectedPreOrder.totalPacks || 0}
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faWeightScale} /> Total Weight:{" "}
                    {selectedPreOrder.totalQuantityKg || 0} kg
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                  </span>
                  <span>
                    <FontAwesomeIcon icon={faAward} /> Premium Quality
                  </span>
                </div>
              </div>

              {/* Payment & Pickup */}
              <div className="detail-section">
                <h3>
                  <FontAwesomeIcon icon={faMoneyBillWave} /> Payment & Pickup
                </h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Payment Method:</span>
                    <span className="detail-value">
                      <FontAwesomeIcon icon={faCreditCard} />{" "}
                      {getPaymentMethodLabel(selectedPreOrder.payment?.method)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Payment Status:</span>
                    <span
                      className={`payment-status ${selectedPreOrder.payment?.status || "pending"}`}
                    >
                      {getStatusLabel(selectedPreOrder.payment?.status)}
                    </span>
                  </div>
                  {selectedPreOrder.payment?.transactionId && (
                    <div className="detail-item">
                      <span className="detail-label">Transaction ID:</span>
                      <span className="detail-value">
                        {selectedPreOrder.payment.transactionId}
                      </span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Pickup Type:</span>
                    <span className="detail-value">
                      {selectedPreOrder.pickup?.type?.replace(/_/g, " ") ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Pickup Address:</span>
                    <span className="detail-value">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                      {selectedPreOrder.pickup?.address || "30 Kissy Road, Freetown, Sierra Leone"}
                    </span>
                  </div>
                  {selectedPreOrder.pickup?.preferredDate && (
                    <div className="detail-item">
                      <span className="detail-label">
                        Preferred Pickup Date:
                      </span>
                      <span className="detail-value">
                        {formatDate(selectedPreOrder.pickup.preferredDate)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div className="detail-section">
                <h3>
                  <FontAwesomeIcon icon={faClock} /> Timeline
                </h3>
                <div className="timeline">
                  {selectedPreOrder.timeline?.map((event, index) => (
                    <div key={index} className="timeline-item">
                      <div
                        className="timeline-dot"
                        style={{ background: getStatusColor(event.status) }}
                      />
                      <div className="timeline-content">
                        <div className="timeline-status">
                          {getStatusLabel(event.status)}
                          {event.updatedBy && (
                            <span className="updated-by">
                              by {event.updatedBy}
                            </span>
                          )}
                        </div>
                        {event.description && (
                          <div className="timeline-description">
                            {event.description}
                          </div>
                        )}
                        <div className="timeline-date">
                          {formatDate(event.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              {(selectedPreOrder.customerNotes ||
                selectedPreOrder.adminNotes) && (
                <div className="detail-section">
                  <h3>
                    <FontAwesomeIcon icon={faInfoCircle} /> Notes
                  </h3>
                  {selectedPreOrder.customerNotes && (
                    <div className="note-item customer">
                      <span className="note-label">Customer:</span>
                      <p>{selectedPreOrder.customerNotes}</p>
                    </div>
                  )}
                  {selectedPreOrder.adminNotes && (
                    <div className="note-item admin">
                      <span className="note-label">Admin:</span>
                      <p>{selectedPreOrder.adminNotes}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Source */}
              {selectedPreOrder.source && (
                <div className="detail-section">
                  <h3>
                    <FontAwesomeIcon icon={faInfoCircle} /> Source
                  </h3>
                  <div className="source-info">
                    <span className="source-badge">
                      <FontAwesomeIcon icon={faGlobe} />{" "}
                      {selectedPreOrder.source?.replace(/_/g, " ") || "Website"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setSelectedPreOrder(null)}
              >
                Close
              </button>
              <a
                href={`https://wa.me/${selectedPreOrder.customerInfo?.phone || "23231030808"}?text=Hi%20${encodeURIComponent(selectedPreOrder.customerInfo?.fullName || "")}%2C%20this%20is%20Kenali%20Enterprise%20regarding%20your%20pre-order%20${encodeURIComponent(selectedPreOrder.preOrderNumber || "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp"
              >
                <FontAwesomeIcon icon={faWhatsapp} /> Contact Customer
              </a>
              <span className="global-footer-badge">
                <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreOrderManager;